import { Button } from '@crazyegginc/hatch';

import {
  useAuthContext,
  useManageSubscriptionPath,
  useMetric,
  useModal,
  usePermissions,
  useTimeZonedFunctions,
} from '/src/hooks';
import { abbreviatedIntervalType } from '/src/utils/string';
import { addCETag } from '../utils';

import { EnterprisePlanModal } from '../components/modals/EnterprisePlanModal';
import { Price, SectionHeader } from '../components/accountUI';
import { PlanSubscriptionInfo } from '../components/PlanSubscriptionInfo';
import { AppLink } from '/src/features/_global/components/AppLink';

import {
  PLAN_TYPES,
  SUBSCRIPTION_STATES,
  PLAN_PLATFORMS,
  FEATURES,
  CAPABILITY_REASONS,
  USE_STRIPE_PRICING_TABLE,
} from '/src/features/_global/constants';

const shouldOpenInNewTab = (isStripeUser) => !USE_STRIPE_PRICING_TABLE && isStripeUser;

const { CANCELING, CANCELED, EXPIRING, EXPIRED } = SUBSCRIPTION_STATES;

export function YourPlan({ currentPlanTitle }) {
  const { currentAccount, subscription } = useAuthContext();
  const isStripeUser = Boolean(currentAccount?.useStripe);
  const isFree = subscription.plan.type === PLAN_TYPES.FREE;

  return (
    <>
      <SectionHeader>Your plan</SectionHeader>

      <div className="flex w-full space-x-5">
        <PlanInfoPanel currentPlanTitle={currentPlanTitle} isStripeUser={isStripeUser} />
        {isFree ? (
          <BillingUpgradeCtaPanel subscription={subscription} isStripeUser={isStripeUser} />
        ) : (
          <NextBillingPanel />
        )}
      </div>
    </>
  );
}

function PlanInfoPanel({ currentPlanTitle, isStripeUser = false }) {
  const modal = useModal();
  const trackMetric = useMetric();
  const { currentAccount, currentUser, subscription } = useAuthContext();
  const {
    paymentPlan: { id: paymentPlanId },
    intervalType,
    id,
  } = currentUser.subscriptions.snapshots;
  const { isAfter } = useTimeZonedFunctions();

  const isTrial = subscription.plan.type === PLAN_TYPES.TRIAL;
  const isShopify = subscription.plan.platform === PLAN_PLATFORMS.SHOPIFY;
  const shopifyLink = currentAccount.shopifyDomain
    ? `https://${currentAccount.shopifyDomain}/admin/apps`
    : 'https://www.shopify.com';

  const expiring15dayTrials =
    subscription.endDate && isAfter(subscription.endDate, new Date()) && [181, 197].includes(paymentPlanId);
  const SUBSCRIPTION_LINK = useManageSubscriptionPath();

  const permissions = usePermissions();
  const canCancel = permissions.can('cancel', FEATURES.BILLING).allowed;
  const { allowed: canChangePlan, reason } = permissions.can('changePlan', FEATURES.BILLING);

  return (
    <section className="flex w-2/3 flex-col justify-between rounded-md bg-white-lilac-500 p-[35px]">
      <div className="flex justify-between">
        <div>
          <h3 className="text-header-2 mb-2.5" id="currentPlan">
            {currentPlanTitle} Plan
          </h3>
          <PlanSubscriptionInfo currentPlanTitle={currentPlanTitle} />
        </div>
        <Price
          amount={Number(subscription.plan.price).toLocaleString()}
          period={abbreviatedIntervalType(intervalType)}
        />
      </div>
      <div className="flex">
        {canChangePlan ? (
          <Button
            component={AppLink}
            to={SUBSCRIPTION_LINK}
            className="!mt-[30px] !self-start"
            onMouseDown={() => {
              addCETag('non_enterprise_change_plan');
              if (isTrial) {
                trackMetric('upgrade_cta_click', 'trial_choose_plan_button');
              }
            }}
            target={shouldOpenInNewTab(isStripeUser) ? '_blank' : '_self'}
            rel={shouldOpenInNewTab(isStripeUser) ? 'noopener noreferrer' : undefined}
          >
            {[CANCELING, CANCELED, EXPIRING, EXPIRED].includes(subscription.state) && !expiring15dayTrials
              ? 'Choose a plan to re-subscribe'
              : isTrial
                ? 'Upgrade Now'
                : 'Change Plan'}
          </Button>
        ) : reason === CAPABILITY_REASONS.CONTACT_SUPPORT ? (
          <Button
            onClick={() => {
              addCETag('enterprise_change_plan');
              modal.show(<EnterprisePlanModal />);
            }}
            className="!mt-[30px] !self-start"
          >
            Change Plan
          </Button>
        ) : null}

        {canCancel && (
          <>
            {isShopify ? (
              <Button
                className="!mt-[30px] !self-start"
                variant="cancel"
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                href={shopifyLink}
              >
                Cancel subscription
              </Button>
            ) : (
              <Button
                component={AppLink}
                to={`/subscriptions/${id}/cancel`}
                className="!mt-[30px] !self-start"
                variant="cancel"
                target={shouldOpenInNewTab(isStripeUser) ? '_blank' : '_self'}
                rel={shouldOpenInNewTab(isStripeUser) ? 'noopener noreferrer' : undefined}
              >
                Cancel subscription
              </Button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function NextBillingPanel() {
  const { subscription } = useAuthContext();
  const { formatDay } = useTimeZonedFunctions();
  const isShopify = subscription.plan.platform === PLAN_PLATFORMS.SHOPIFY;

  let nextPayment = 'N/A';

  if (subscription.state === EXPIRED) {
    nextPayment = 'N/A';
  } else if ([CANCELED, CANCELING].includes(subscription.state)) {
    nextPayment = `Switching to Free Plan`;
  } else if (subscription.endDate && subscription.endDate !== -1) {
    nextPayment = formatDay(subscription.endDate);
  }

  return (
    <section className="flex w-1/3 flex-col justify-between rounded-md bg-white-lilac-500 p-[35px]">
      <div>
        <h3 className="text-body-4">Next payment</h3>
        {isShopify ? 'Billing via Shopify' : <div className="text-header-4 mt-2.5">{nextPayment}</div>}
      </div>
      {!isShopify && (
        <Button component={AppLink} to={`/account/billing`} className="!mt-[30px] !self-start" variant="secondary">
          Manage billing
        </Button>
      )}
    </section>
  );
}

function BillingUpgradeCtaPanel({ isStripeUser = false }) {
  const trackMetric = useMetric();
  const subscriptionLink = useManageSubscriptionPath();

  // TODO PRICING_V2: UPDATE THE URL TO BE MANAGE_PAYMENT_EXTERNAL FROM CONSTANTS.JS
  return (
    <div className="flex w-1/3 flex-col justify-between rounded-md bg-riptide-500 p-[35px] pt-[30px] text-black-pearl-500">
      <h3 className="text-header-5">Want Heatmaps, Recordings, Traffic Analysis, A/B Testing & Error Tracking?</h3>

      <p className="text-body-2 mt-2.5">Upgrade your plan to get the full suite of Crazy Egg features.</p>

      <Button
        component={AppLink}
        to={subscriptionLink}
        className="text-body-2 !mt-3.75 !h-[30px] min-w-[184px] !self-start !bg-black-pearl-500 !text-white hover:!bg-black-pearl-500"
        onMouseDown={() => trackMetric('upgrade_cta_click', 'plan_view_and_compare_plans')}
        target={shouldOpenInNewTab(isStripeUser) ? '_blank' : '_self'}
        rel={shouldOpenInNewTab(isStripeUser) ? 'noopener noreferrer' : undefined}
      >
        View and compare plans
      </Button>
    </div>
  );
}
